import "./styles.scss";

import { Fragment, useState } from "react";
import classnames from "classnames";
import { useTranslation } from "react-i18next";

import { useApp } from "context/app";
import { useNewWebsite } from "context/client/new_website";

import AnimatedGradient from "components/AnimatedGradient";

import gradient_props from "libs/gradient_props";

import {
  DomainModal,
  PlanModal,
  ContactModal,
  BillingModal,
} from "./components";
import NavMobile from "../NavMobile";

import useResponsive from "libs/helpers/useResponsive";
import plans_data from "libs/plans_data";
import priceRender from "libs/helpers/priceRender";
import getUserData from "libs/helpers/getUserData";

import { ReactComponent as ArrowIcon } from "assets/icons/new/forward-arrow.svg";
import { ReactComponent as ViewIcon } from "assets/icons/new/view.svg";

const mainClass = "new-website-content__widget";

const Widget = ({ isNextDisabled, isPayment, isLoading, onClick }) => {
  const { t, i18n } = useTranslation();
  const { plans_list, theme } = useApp();
  const { min } = useResponsive();

  const { time, seed, colors } = gradient_props.blue;
  const { step, form_data, change_plan_mode, prevStep, website } =
    useNewWebsite();

  const [isModalOpen, setIsModalOpen] = useState({
    domain: false,
    plan: false,
    contact: false,
    billing: false,
  });

  const domain = form_data?.website;
  const plan = form_data?.plan;
  const billed_type = form_data?.billed_type;
  const { contact_data, billing_data } = getUserData(form_data, i18n.language);

  const plans = plans_list?.map((item, index) => ({
    ...plans_data?.[index],
    ...item,
  }));

  const current_plan =
    plans?.length > 0 && plan && website?.subscription?.price_id
      ? plans.find((p) =>
          p.prices.some((price) => price.id === website.subscription.price_id),
        )
      : null;
  const current_plan_price = current_plan
    ? current_plan.prices.find(
        (price) => price.id === website.subscription.price_id,
      )
    : null;

  const selected_plan =
    plans?.length > 0 && plan
      ? plans?.find((p) => p.prices.some((price) => price.id === plan))
      : null;
  const selected_plan_price = selected_plan
    ? selected_plan.prices.find((price) => price.id === plan)
    : null;

  const next_payment_date = website?.subscription?.next_payment
    ? new Date(website.subscription.next_payment * 1000)
        .toLocaleDateString(i18n.language, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, ".")
    : new Date()
        .toLocaleDateString(i18n.language, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        })
        .replace(/\//g, ".");

  const handleModalOpen = (type) =>
    setIsModalOpen((prev) => ({
      ...prev,
      [type]: true,
    }));

  const handleModalClose = (type) =>
    setIsModalOpen((prev) => ({ ...prev, [type]: false }));

  return (
    <>
      <div
        className={classnames(mainClass, {
          [`${mainClass}--payment`]: isPayment,
        })}
      >
        <h5>{t("Checkout")}</h5>
        <Item
          label="Domain"
          onClick={
            !!!change_plan_mode ? () => handleModalOpen("domain") : undefined
          }
        >
          <p>{domain}</p>
        </Item>
        {selected_plan && step > 1 && (
          <Item
            variant="plan"
            label="Subscription plan"
            onClick={() => handleModalOpen("plan")}
          >
            {current_plan && (
              <>
                <p className={`${mainClass}-label`}>{t("Change from")}</p>
                <Plan
                  icon={current_plan.icon}
                  name={current_plan.name}
                  price={priceRender(
                    current_plan_price?.price?.amount / 100,
                    current_plan_price?.price?.currency,
                  )}
                  billed_type={website?.subscription?.contract_period?.toLowerCase()}
                />
                <p className={`${mainClass}-label`}>{t("to")}</p>
              </>
            )}
            <Plan
              icon={selected_plan.icon}
              name={selected_plan.name}
              price={priceRender(
                selected_plan_price?.price?.amount / 100,
                selected_plan_price?.price?.currency,
              )}
              billed_type={billed_type}
            />
            {current_plan && (
              <p className={`${mainClass}-label`}>
                {t("Will take effect from")} {next_payment_date}.
              </p>
            )}
          </Item>
        )}
        {contact_data?.length > 0 && step > 2 && (
          <Item
            label="Contact data"
            onClick={
              !!!change_plan_mode ? () => handleModalOpen("contact") : undefined
            }
          >
            <Data data={contact_data} />
          </Item>
        )}
        {billing_data?.length > 0 && step > 2 && (
          <Item
            label="Invoice details"
            onClick={
              !!!change_plan_mode ? () => handleModalOpen("billing") : undefined
            }
          >
            <Data data={billing_data} />
          </Item>
        )}

        {min.lg ? (
          <>
            <div className={`${mainClass}__cta`}>
              <button
                type={typeof onClick === "function" ? "button" : "submit"}
                disabled={isNextDisabled || isLoading}
                onClick={onClick}
              >
                {t(isPayment ? "Proceed to payment" : "Next")}
                {isPayment && (
                  <i>
                    <ViewIcon />
                  </i>
                )}
              </button>
            </div>

            <div className={`${mainClass}__background`}>
              <AnimatedGradient
                id="widget-gradient"
                time={time}
                seed={seed}
                colors={colors[theme]}
              />
            </div>
          </>
        ) : (
          <div className={`${mainClass}__nav-mobile`}>
            <NavMobile isPayment onBack={() => prevStep()} onClick={onClick} />
          </div>
        )}
      </div>

      <PlanModal
        current={{
          plan: website?.subscription?.price_id,
          billed_type: website?.subscription?.contract_period?.toLowerCase(),
        }}
        isOpen={isModalOpen.plan}
        onClose={() => handleModalClose("plan")}
      />

      {!!!change_plan_mode && (
        <>
          <DomainModal
            isOpen={isModalOpen.domain}
            onClose={() => handleModalClose("domain")}
          />
          <ContactModal
            isOpen={isModalOpen.contact}
            onClose={() => handleModalClose("contact")}
          />
          <BillingModal
            isOpen={isModalOpen.billing}
            onClose={() => handleModalClose("billing")}
          />
        </>
      )}
    </>
  );
};

const Item = ({ variant, label, children, onClick }) => {
  const { t } = useTranslation();

  return (
    <div
      className={classnames(`${mainClass}__item`, {
        [`${mainClass}__item--${variant}`]: !!variant,
      })}
    >
      {label && <p>{t(label)}</p>}
      <div className={`${mainClass}__item-content`}>{children}</div>
      {typeof onClick === "function" && (
        <button type="button" onClick={onClick}>
          {t("Edit")}
          <i>
            <ArrowIcon />
          </i>
        </button>
      )}
    </div>
  );
};

const Plan = ({ icon, name, price, billed_type }) => {
  const { t } = useTranslation();

  return (
    <div className={`${mainClass}__plan`}>
      <i>{icon}</i>
      <div>
        <p>{name}</p>
        <p>
          {price} {t("per Month")} {t("with tax").replace(/\s+/g, "\u00a0")}
        </p>
        <p>
          ({t(billed_type === "yearly" ? "billed yearly" : "billed monthly")})
        </p>
      </div>
    </div>
  );
};

const Data = ({ data }) => {
  return (
    <p>
      {data.map((item, index) => (
        <Fragment key={index}>
          {item}
          {index !== data.length - 1 && <br />}
        </Fragment>
      ))}
    </p>
  );
};

export default Widget;
